import { apiFetch } from './client';

export interface AdminClient {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  disabled_at: string | null;
  entra_group_id?: string | null;
  entra_group_name?: string | null;
  entra_synced_at?: string | null;
  external_tenant_id?: string | null;
  external_group_id?: string | null;
  external_display_name?: string | null;
  external_email_domains?: string | null;
  signin_method?: 'password' | 'sso' | 'either';
}

export interface EntraGroup { id: string; displayName: string; }
export interface GroupSyncResult { members: number; added: number; removed: number; raw_members?: number; skipped_non_staff?: number; skipped?: boolean; }

export interface AdminModule {
  module_id: string;
  code: string;
  name: string;
  enabled: boolean;
  /** On, the module is visible to MI staff only. Absent from an API older than migration 0057. */
  staff_only?: boolean;
}

export interface AdminInvite {
  id: string;
  email: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  client_slug: string;
  client_name: string;
}

export interface AdminClientUser {
  id: string | null;
  email: string;
  display_name: string | null;
  title: string | null;
  last_login_at: string | null;
  disabled_at: string | null;
  role: string | null;
  joined_at: string;
  status: 'active' | 'invited';
  invite_token: string | null;
  invite_expires_at: string | null;
}

export interface AdminLoginEvent {
  id: string;
  user_id: string | null;
  email: string | null;
  display_name: string | null;
  source: 'staff' | 'client';
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditEvent {
  id: string;
  module: string;
  item_id: string | null;
  item_label: string | null;
  action: string;
  action_label: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
}

export interface AssignableUser {
  id: string;
  email: string;
  display_name: string | null;
  source: 'admin' | 'client_admin' | 'client_user';
}

export type StaffRole = 'admin' | 'client_admin';
export interface AdminStaffUser {
  id: string;
  email: string;
  display_name: string | null;
  role: StaffRole | null;
  staff_sub: string | null;
  last_login_at: string | null;
  disabled_at: string | null;
  assigned_clients: { client_id: string; slug: string; name: string }[];
}
export interface ClientverseVersion {
  id: number;
  version_string: string;
  major: number | null;
  minor: number | null;
  patch: number | null;
  build: number | null;
  namespace_prefix: string | null;
  first_synced_at: string;
  last_synced_at: string;
}
export interface ClientClientverseState {
  pinned_version_id: number | null;
  pinned_version_string: string | null;
  latest_version_id: number | null;
  latest_version_string: string | null;
}

// One entry per named/labeled object or field change reported by the
// ClientVerse Core change-history endpoint.
export interface ClientverseHistoryObjectChange {
  name: string;
  label: string | null;
  archived_at?: string | null;
}
export interface ClientverseHistoryFieldChange {
  object_name: string;
  name: string;
  label: string | null;
  archived_at?: string | null;
}
export interface ClientverseHistoryVersion {
  id: number;
  version_string: string;
  first_synced_at: string;
  is_baseline: boolean;
  total_objects: number;
  total_fields: number;
  new_objects: ClientverseHistoryObjectChange[];
  new_fields: ClientverseHistoryFieldChange[];
  deprecated_objects: ClientverseHistoryObjectChange[];
  deprecated_fields: ClientverseHistoryFieldChange[];
}
export interface ClientverseHistory {
  versions: ClientverseHistoryVersion[];
}

// ClientVerse Core "Browse & Document" — every CORE object/field (including
// deprecated ones), not scoped to a single version. first/last_version_*
// carry when each row was introduced/last confirmed present.
export interface ClientverseCoreBrowseField {
  id: string;
  name: string;
  label: string | null;
  data_type: string | null;
  is_required: boolean;
  is_custom: boolean;
  length: number | null;
  reference_to: string[];
  description: string | null;
  sample_data: string | null;
  first_version_id: number | null;
  first_version_string: string | null;
  last_version_id: number | null;
  last_version_string: string | null;
  archived_at: string | null;
}
export interface ClientverseCoreBrowseObject {
  id: string;
  name: string;
  label: string | null;
  is_custom: boolean;
  queryable: boolean;
  description: string | null;
  first_version_id: number | null;
  first_version_string: string | null;
  last_version_id: number | null;
  last_version_string: string | null;
  archived_at: string | null;
  fields: ClientverseCoreBrowseField[];
}
export interface ClientverseCoreBrowse {
  objects: ClientverseCoreBrowseObject[];
}

// Canonical staging templates (global, client_id IS NULL in source_schemas)
export interface StagingCanonicalVersion {
  id: string;
  schema_version: string;
  schema_id: string | null;
  system_name: string | null;
  description: string | null;
  uploaded_at: string;
  objects_count: number;
  fields_count: number;
  /** Clients whose own staging schema was last started from this template. */
  based_clients: number;
}

export interface ClientStagingState {
  /** The client's own staging schema, or null if it has none yet. */
  schema: { schema_version: string | null; based_on_version: string | null; uploaded_at: string; objects_count: number; fields_count: number } | null;
  /** Canonical templates it can be started from, newest first. */
  templates: { schema_version: string; uploaded_at: string }[];
}

/** What starting a client from a template would do (dry run) or did. */
export interface StagingTemplatePlan {
  dryRun: boolean;
  schema_version: string;
  tablesAdded: string[];
  tablesRemoved: string[];
  fieldsAdded: string[];
  fieldsRemoved: string[];
  danglingMappings: { mappingKey: string; mapping: string; missing: string }[];
}

// Body accepted by POST /admin/staging-canonical/versions — mirrors the shape
// used for per-client staging uploads plus a required schema_version.
export interface StagingCanonicalUploadBody {
  schema_version: string;
  schema_id?: string | null;
  system_name?: string | null;
  description?: string | null;
  objects: Array<{
    name: string;
    label?: string | null;
    is_custom?: boolean;
    queryable?: boolean;
    description?: string | null;
    tier?: number | null;
    sf_target?: string | null;
    source?: string | null;
    fields?: Array<{
      name: string;
      label?: string | null;
      data_type?: string | null;
      sql_type?: string | null;
      is_required?: boolean;
      is_custom?: boolean;
      is_primary_key?: boolean;
      is_identity?: boolean;
      length?: number | null;
      reference_to?: string[];
      picklist_values?: unknown[];
      source_expression?: string | null;
      description?: string | null;
      // Illustrative example value shown in the Browse & Document editor.
      // Populated by staff so downstream users can see "what a value looks
      // like" without an actual row.
      sample_data?: string | null;
      // ClientVerse fills this field; the client leaves it empty.
      from_clientverse?: boolean;
    }>;
  }>;
}
export interface ClientverseSchemaExport {
  clientverse_version_id: number;
  clientverse_version: string;
  namespace_prefix: string | null;
  exported_at: string;
  objects: Array<{
    name: string;
    label: string | null;
    is_custom: boolean;
    queryable: boolean;
    fields: Array<{
      name: string;
      label: string | null;
      data_type: string | null;
      is_required: boolean;
      is_custom: boolean;
      length: number | null;
      reference_to: string[];
      picklist_values: Array<{
        value: string;
        label: string | null;
        is_active: boolean;
        is_default: boolean;
        sort_order: number | null;
      }>;
    }>;
  }>;
}

export interface ClientAdminRow {
  id: string;
  email: string;
  display_name: string | null;
  role: StaffRole | null;
  access_source: 'global' | 'assigned';
  manual: boolean;
  via_group: boolean;
}

export const adminApi = {
  listClients: (t: string | null) => apiFetch<AdminClient[]>('/admin/clients', t),
  getClient: (t: string | null, clientId: string) => apiFetch<AdminClient>(`/admin/clients/${clientId}`, t),
  createClient: (t: string | null, body: { slug: string; name: string }) =>
    apiFetch<AdminClient>('/admin/clients', t, { method: 'POST', body: JSON.stringify(body) }),

  listModules: (t: string | null, clientId: string) =>
    apiFetch<AdminModule[]>(`/admin/clients/${clientId}/modules`, t),
  toggleModule: (t: string | null, clientId: string, moduleId: string, enabled: boolean, staffOnly?: boolean) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/modules/${moduleId}`, t, {
      method: 'PUT',
      body: JSON.stringify(staffOnly === undefined ? { enabled } : { enabled, staffOnly }),
    }),

  listClientUsers: (t: string | null, clientId: string) =>
    apiFetch<AdminClientUser[]>(`/admin/clients/${clientId}/users`, t),
  listAssignableUsers: (t: string | null, clientId: string) =>
    apiFetch<AssignableUser[]>(`/admin/clients/${clientId}/assignable-users`, t),
  clientAudit: (t: string | null, clientId: string, page: number, pageSize: number = 50) =>
    apiFetch<{ page: number; pageSize: number; total: number; items: AuditEvent[] }>(
      `/admin/clients/${clientId}/audit?page=${page}&pageSize=${pageSize}`, t,
    ),
  createClientUser: (t: string | null, clientId: string, body: { email: string; display_name: string; title?: string }) =>
    apiFetch<{ invite: { id: string; email: string; display_name: string; title: string | null; expires_at: string }; token: string; acceptUrl: string }>(
      `/admin/clients/${clientId}/users`, t, { method: 'POST', body: JSON.stringify(body) },
    ),
  setUserDisabled: (t: string | null, clientId: string, userId: string, disabled: boolean) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/users/${userId}/disabled`, t, {
      method: 'PUT',
      body: JSON.stringify({ disabled }),
    }),

  loginAudit: (t: string | null, page: number, pageSize: number = 50) =>
    apiFetch<{ page: number; pageSize: number; total: number; items: AdminLoginEvent[] }>(
      `/admin/audit/logins?page=${page}&pageSize=${pageSize}`, t,
    ),

  listStaffUsers: (t: string | null) => apiFetch<AdminStaffUser[]>('/admin/users', t),
  createStaffUser: (t: string | null, body: { email: string; display_name?: string; role: StaffRole; client_ids?: string[] }) =>
    apiFetch<{ id: string }>('/admin/users', t, { method: 'POST', body: JSON.stringify(body) }),
  updateStaffUser: (t: string | null, userId: string, body: { role?: StaffRole | null; display_name?: string; client_ids?: string[]; disabled?: boolean }) =>
    apiFetch<{ ok: true }>(`/admin/users/${userId}`, t, { method: 'PUT', body: JSON.stringify(body) }),

  listClientAdmins: (t: string | null, clientId: string) =>
    apiFetch<ClientAdminRow[]>(`/admin/clients/${clientId}/client-admins`, t),
  addClientAdmin: (t: string | null, clientId: string, userId: string) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/client-admins`, t, {
      method: 'POST', body: JSON.stringify({ userId }),
    }),
  removeClientAdmin: (t: string | null, clientId: string, userId: string) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/client-admins/${userId}`, t, { method: 'DELETE' }),

  searchEntraGroups: (t: string | null, q: string) =>
    apiFetch<EntraGroup[]>(`/admin/entra-groups?q=${encodeURIComponent(q)}`, t),
  linkEntraGroup: (t: string | null, clientId: string, groupId: string | null) =>
    apiFetch<{ ok: true; linked: { id: string; name: string } | null; sync?: GroupSyncResult }>(
      `/admin/clients/${clientId}/entra-group`, t, { method: 'PUT', body: JSON.stringify({ group_id: groupId }) },
    ),
  syncEntraGroup: (t: string | null, clientId: string) =>
    apiFetch<GroupSyncResult>(`/admin/clients/${clientId}/sync-group`, t, { method: 'POST' }),

  updateClientSso: (t: string | null, clientId: string, body: {
    external_tenant_id?: string | null;
    external_group_id?: string | null;
    external_display_name?: string | null;
    external_email_domains?: string | null;
    signin_method?: 'password' | 'sso' | 'either';
  }) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/sso`, t, { method: 'PUT', body: JSON.stringify(body) }),

  listClientverseVersions: (t: string | null) =>
    apiFetch<ClientverseVersion[]>('/admin/clientverse/versions', t),
  getClientverseHistory: (t: string | null) =>
    apiFetch<ClientverseHistory>('/admin/clientverse/history', t),
  getClientverseCoreBrowse: (t: string | null) =>
    apiFetch<ClientverseCoreBrowse>('/admin/clientverse/core/browse', t),
  updateClientverseCoreObjectDescription: (t: string | null, objectId: string, description: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clientverse/core/objects/${objectId}/description`, t, {
      method: 'PUT', body: JSON.stringify({ description }),
    }),
  updateClientverseCoreFieldDescription: (t: string | null, fieldId: string, description: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clientverse/core/fields/${fieldId}/description`, t, {
      method: 'PUT', body: JSON.stringify({ description }),
    }),
  updateClientverseCoreFieldSampleData: (t: string | null, fieldId: string, sample_data: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clientverse/core/fields/${fieldId}/sample-data`, t, {
      method: 'PUT', body: JSON.stringify({ sample_data }),
    }),
  exportClientverseSchema: (t: string | null, versionId: number) =>
    apiFetch<ClientverseSchemaExport>(`/admin/clientverse/versions/${versionId}/export`, t),
  getClientClientverse: (t: string | null, clientId: string) =>
    apiFetch<ClientClientverseState>(`/admin/clients/${clientId}/clientverse`, t),
  setClientClientversePin: (t: string | null, clientId: string, versionId: number | null) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/clientverse`, t, {
      method: 'PUT',
      body: JSON.stringify({ version_id: versionId }),
    }),

  // Canonical staging templates (global)
  listStagingCanonicalVersions: (t: string | null) =>
    apiFetch<StagingCanonicalVersion[]>('/admin/staging-canonical/versions', t),
  uploadStagingCanonicalVersion: (t: string | null, body: StagingCanonicalUploadBody) =>
    apiFetch<{ id: string; schema_version: string; objects_loaded: number; fields_loaded: number }>(
      '/admin/staging-canonical/versions', t,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  exportStagingCanonicalVersion: (t: string | null, version: string) =>
    apiFetch<StagingCanonicalUploadBody>(`/admin/staging-canonical/versions/${encodeURIComponent(version)}/export`, t),
  deleteStagingCanonicalVersion: (t: string | null, version: string) =>
    apiFetch<{ ok: true; schema_version: string }>(
      `/admin/staging-canonical/versions/${encodeURIComponent(version)}`, t, { method: 'DELETE' },
    ),
  // Update the `description` on a specific object or field within a canonical
  // staging version. Admin-only. Used by the documentation editor.
  updateStagingCanonicalObjectDescription: (t: string | null, version: string, objectName: string, description: string | null) =>
    apiFetch<{ ok: true }>(
      `/admin/staging-canonical/versions/${encodeURIComponent(version)}/objects/${encodeURIComponent(objectName)}/description`,
      t,
      { method: 'PUT', body: JSON.stringify({ description }) },
    ),
  updateStagingCanonicalFieldDescription: (t: string | null, version: string, objectName: string, fieldName: string, description: string | null) =>
    apiFetch<{ ok: true }>(
      `/admin/staging-canonical/versions/${encodeURIComponent(version)}/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/description`,
      t,
      { method: 'PUT', body: JSON.stringify({ description }) },
    ),
  // Sample data (illustrative example value) is stored alongside description
  // and edited from the same Browse & Document page.
  updateStagingCanonicalFieldSampleData: (t: string | null, version: string, objectName: string, fieldName: string, sample_data: string | null) =>
    apiFetch<{ ok: true }>(
      `/admin/staging-canonical/versions/${encodeURIComponent(version)}/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/sample-data`,
      t,
      { method: 'PUT', body: JSON.stringify({ sample_data }) },
    ),
  updateStagingCanonicalFieldFromClientverse: (t: string | null, version: string, objectName: string, fieldName: string, from_clientverse: boolean) =>
    apiFetch<{ ok: true }>(
      `/admin/staging-canonical/versions/${encodeURIComponent(version)}/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/from-clientverse`,
      t,
      { method: 'PUT', body: JSON.stringify({ from_clientverse }) },
    ),

  // A client's own staging schema
  getClientStaging: (t: string | null, clientId: string) =>
    apiFetch<ClientStagingState>(`/admin/clients/${clientId}/staging`, t),
  /** Replace the client's staging schema with a copy of a template. dryRun previews it. */
  applyStagingTemplate: (t: string | null, clientId: string, schemaVersion: string, dryRun: boolean) =>
    apiFetch<StagingTemplatePlan>(`/admin/clients/${clientId}/staging/from-canonical`, t, {
      method: 'POST',
      body: JSON.stringify({ schema_version: schemaVersion, dryRun }),
    }),
  /** The client's own staging schema, sample data included, as the canonical export shapes it. */
  exportClientStaging: (t: string | null, clientId: string) =>
    apiFetch<StagingCanonicalUploadBody>(`/admin/clients/${clientId}/data-mapping/schemas/staging`, t),
  updateClientStagingObjectDescription: (t: string | null, clientId: string, objectName: string, description: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/staging/objects/${encodeURIComponent(objectName)}/description`, t,
      { method: 'PUT', body: JSON.stringify({ description }) }),
  updateClientStagingFieldDescription: (t: string | null, clientId: string, objectName: string, fieldName: string, description: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/staging/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/description`, t,
      { method: 'PUT', body: JSON.stringify({ description }) }),
  updateClientStagingFieldSampleData: (t: string | null, clientId: string, objectName: string, fieldName: string, sample_data: string | null) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/staging/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/sample-data`, t,
      { method: 'PUT', body: JSON.stringify({ sample_data }) }),
  updateClientStagingFieldFromClientverse: (t: string | null, clientId: string, objectName: string, fieldName: string, from_clientverse: boolean) =>
    apiFetch<{ ok: true }>(`/admin/clients/${clientId}/staging/objects/${encodeURIComponent(objectName)}/fields/${encodeURIComponent(fieldName)}/from-clientverse`, t,
      { method: 'PUT', body: JSON.stringify({ from_clientverse }) }),

  listInvites: (t: string | null) => apiFetch<AdminInvite[]>('/admin/invites', t),
  createInvite: (t: string | null, body: { clientId: string; email: string; expiresInHours?: number }) =>
    apiFetch<{ invite: AdminInvite; token: string; acceptUrl: string }>('/admin/invites', t, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
